// The browser's copy of the cookie/analytics choice, and the only module that touches
// localStorage for it.
//
// WHY THIS EXISTS AS A MODULE (Wave C item C1.10). It used to be three consts and two
// effects inside App.js, which was fine while App was the only thing that could change
// the choice. It no longer is: the Privacy Choices panel can now withdraw `analytics`
// server-side, and if that did not also reach this store, the screen would say "no
// analytics are loaded and nothing is transferred outside India" while GA4 carried on
// loading in that very browser. A promise the product does not keep is worse than one it
// never made, so both writers go through here and App re-reads on notify.
//
// WHAT THIS STORE IS, AND IS NOT. It is the MECHANISM: GA4 loads per-browser, so the
// decision has to live where the script tag can see it. It is not the RECORD - that is
// `user_consents` on the server, which is what an export or a regulator can be shown.
// The two are reconciled at login (see App.js). Where they disagree, an explicit click
// wins over stored state, because a click is a fresh act of consent and a stored value
// is only the memory of one.

export const COOKIE_CONSENT_KEY = 'cookieConsentPrefs_v2';

// Anything that is not recognisably a v2 preference object reads as "no decision yet",
// which shows the banner again rather than guessing. A half-parsed consent is not
// consent.
export const normalizeCookiePreferences = (rawPreferences) => {
  if (!rawPreferences || typeof rawPreferences !== 'object') {
    return null;
  }

  const hasGranularFlags =
    typeof rawPreferences.analyticsUsage === 'boolean' &&
    typeof rawPreferences.analyticsDevice === 'boolean';

  if (!hasGranularFlags) {
    return null;
  }

  return {
    necessary: true,
    analyticsUsage: Boolean(rawPreferences.analyticsUsage),
    analyticsDevice: Boolean(rawPreferences.analyticsDevice),
    consentedAt: rawPreferences.consentedAt || new Date().toISOString(),
  };
};

// THE MAPPING FROM TWO CHECKBOXES TO ONE PURPOSE. The banner offers usage analytics and
// device diagnostics separately; the consent registry has a single `analytics` purpose.
// That is not a mismatch to paper over - it is the right reduction for the question the
// server has to answer, which is s11(1)(c)'s "was anything shared with Google LLC". Either
// box does that, so either box means granted.
//
// The cost, stated rather than hidden: the server row cannot tell the two apart, so
// re-granting from Privacy Choices turns both back on. The granular choice stays a
// browser-level control, and the Privacy Choices copy points at it.
export const analyticsEnabled = (preferences) =>
  Boolean(preferences && (preferences.analyticsUsage || preferences.analyticsDevice));

export const readCookiePreferences = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    return normalizeCookiePreferences(JSON.parse(stored));
  } catch {
    // A private window, cleared site data, or a corrupt value. Treated as no decision,
    // which is the safe direction: the banner reappears and nothing loads until asked.
    return null;
  }
};

const listeners = new Set();

// In-tab only, deliberately. `storage` events fire cross-tab but never in the tab that
// wrote, which is the tab that actually needs to re-render - so this is a plain
// subscription rather than a wrapper around an event that would not fire when it matters.
export const subscribeToCookiePreferences = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Returns the normalized value actually stored, so a caller never has to re-derive what
// it just wrote. Storage failing does not stop the notification: the choice still governs
// this page load even if it cannot be remembered for the next one.
export const saveCookiePreferences = (preferences) => {
  const nextPreferences = {
    necessary: true,
    analyticsUsage: Boolean(preferences?.analyticsUsage),
    analyticsDevice: Boolean(preferences?.analyticsDevice),
    consentedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(nextPreferences));
  } catch (error) {
    console.error('Failed to save cookie preferences:', error);
  }

  listeners.forEach((listener) => listener(nextPreferences));
  return nextPreferences;
};

// Used by the Privacy Choices panel, where the user is turning the single `analytics`
// purpose on or off rather than picking categories. Off means off for both; on means the
// same as the banner's "Accept All", which is what the copy there tells them.
export const setAnalyticsPreference = (enabled) => saveCookiePreferences({
  analyticsUsage: Boolean(enabled),
  analyticsDevice: Boolean(enabled),
});
